import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Container, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useCompanyStore, useBlogCategoriesStore } from 'stores';
import { useTrackVisibility } from 'react-intersection-observer-hook';
//api
import { getActivities } from 'api/company';
import { slidersClient } from 'api/products';
import { getAllLists } from 'api/lists';
import NoLists from 'components/userzone/NoLists';
//components
import ListHome from './listHome';
import PromotionalBanner from './bannerPromotional';
import SectionAffiliate from './sectionAffiliate';
import EventHome from './events';
import QuickOrderModal from './QuickOrderModal';
import Head from 'components/metatags_helmet/metatags';
import SectionLoader from 'components/loader/SectionLoader';
import PaginationLoader from 'components/loader/PaginationLoader';
import Slider from 'components/slider';

const Home = () => {
  const { i18n } = useTranslation();
  const [sliders, setSliders] = useState([]);
  const [events, setEvents] = useState([]);
  const [lists, setLists] = useState([]);
  const [showQuickOrder, setShowQuickOrder] = useState(false);
  const { t } = useTranslation(['home']);
  const company = useCompanyStore();
  const {
    mainLists,
    descripcion_pub,
    is_single_company,
    has_slider,
    has_events,
    home_image,
    setMainLists
  } = company;
  const { fetchBlogCategories } = useBlogCategoriesStore();
  //pagination
  const [ref, { isVisible }] = useTrackVisibility();
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [next, setNext] = useState(null);
  const [maxPage, setMaxPage] = useState(null);
  const [page, setPage] = useState(1);

  const loadEvents = useCallback(async () => {
    const response = await getActivities({
      page: 1,
      page_size: 3,
      actives: true,
      ordered_by: '-start'
    });

    if (response?.status === 200) {
      let events = Array.isArray(response?.data?.results)
        ? [...response.data.results]
        : [];

      events = events
        .sort((a, b) => new Date(b.start_date) - new Date(a.start_date))
        .slice(0, 3);

      setEvents(events);
    } else {
      console.log('Failed to load events:', response);
    }
  }, []);


  const getSlidersClient = useCallback(async () => {
    const { status, data } = await slidersClient({ type_str: "home", active: true });
    if (status === 200) {
      setSliders(data?.results);
    }
  }, []);

  const handleLoadLists = useCallback(async (page = 1) => {
    // Si es la primera página, usar isInitialLoading, sino usar isLoadingMore
    if (page === 1) {
      setIsInitialLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    const payload = {
      page: page,
      page_size: 3,
      location_web: 'home',
      estado: true,
      have_products: true,
      fields: 'id,favorite,products,contenido,nombre,url',
    };
    try {
      const { status, data } = await getAllLists(payload);
      if (status === 200) {
        setLists((prev) => (page === 1 ? data?.results : [...prev, ...data?.results]));
        setMaxPage(data?.total_pages);
        setNext(data?.next);
      }
    } catch (error) {
      console.log(error);
    }
    if (page === 1) {
      setIsInitialLoading(false);
    } else {
      setIsLoadingMore(false);
    }
  }, []);

  const getAllListsAside = useCallback(async () => {
    const payload = {
      location_web: 'none',
      estado: true,
      have_products: true,
      fields: 'id,favorite,contenido,nombre,url',
    };
    try {

      const { status, data } = await getAllLists(payload);

      if (status === 200) {
        setMainLists(data.results);
      }

    } catch (error) {
      console.log(error);
    }
  }, [setMainLists]);

  // first call
  useEffect(() => {
    if(has_slider) {
      getAllListsAside();
    }
    getSlidersClient();
    handleLoadLists();
    fetchBlogCategories();
    goUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goUp = () => {
    window.scrollTo(0, 0);
  };

  const handleOpenQuickOrder = () => {
    setShowQuickOrder(true);
  };

  const handleCloseQuickOrder = () => {
    setShowQuickOrder(false);
  };

  useEffect(() => {
    if (has_events) {
      loadEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [has_events]);

  // next API calls
  useEffect(() => {
    if (page < maxPage && next && isVisible && !isLoadingMore && !isInitialLoading) {
      const getNewItems = async (newValuePage) => {
        await handleLoadLists(newValuePage);
        setPage(newValuePage);
      };
      let newValuePage = page + 1;
      getNewItems(newValuePage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, page, maxPage, next, isLoadingMore, isInitialLoading]);

  return (
    <>
      <Head description={descripcion_pub} title="" home={true} />
      <Container className="container-fluid mainDiv">
        <Row>
          <Col lg={12}>{sliders?.length > 0 && <Slider elements={sliders} />}</Col>
          <Col lg={(events.length > 0 || mainLists.length > 0) ? 9 : 9}>
            {isInitialLoading ? (
              <SectionLoader />
            ) : (
              <>
                {lists?.length > 0 ? (
                  lists.map((item, i) => (
                    <React.Fragment key={`ListHome${i}`}>
                      <ListHome data={item} />
                      {(i === 1 && home_image !== '') && (
                        <PromotionalBanner />
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <NoLists icon={true} />
                )}
                <PaginationLoader visibility={isLoadingMore} />
                {!isInitialLoading && next && <div aria-hidden="true" ref={ref}></div>}
              </>
            )}
          </Col>
          <Col lg={3}>
            <div className="mb-4 quick-order-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0 quick-order-title">{t('quickOrder')}</h5>
              <Button
                className="quick-order-button"
                variant="primary"
                size="sm"
                onClick={handleOpenQuickOrder}
              >
                {t('add')}
              </Button>
            </div>
            {(events.length > 0 || mainLists.length > 0) && (
              <EventHome events={events} lists={mainLists} />
            )}
          </Col>
          <QuickOrderModal
            show={showQuickOrder}
            onClose={handleCloseQuickOrder}
          />
          {
            !is_single_company && (
              <Col md={12} className="mt-5 ">
                <SectionAffiliate />
              </Col>
            )
          }
        </Row>
      </Container>
    </>
  );
};

export default Home;
